import type { IGetActionsData } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import type { ISocialOffer } from "@bbj-types/offer";
import type { ISocialApplyTransition } from "@bbj-types/socialApply";
import { SocialApplyStatus } from "@bbj-types/socialApply/enum";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Chip, ClickAwayListener, Stack, Typography } from "@mui/material";
import type { ICascadingMenuProps } from "@surfaces/CascadingMenu";
import { ChallengesMenu } from "./ChallengesMenu";
import { useFollowUpSocialOffer } from "./logics";
import { MenuProposeSocialOffer } from "./menuProposeSocialOffer";

export interface IFollowUpSocialOfferProps<T extends ISocialOffer> {
  offer: T & { status?: SocialApplyStatus };
  onUpdateOffer?: (
    socialOffer: T & { status?: SocialApplyStatus; transitions?: ISocialApplyTransition[]; socialApplyId?: string },
    generatedAction: IGetActionsData
  ) => void;
  candidate: ICandidate;
  expired: boolean;
}

export const FollowUpSocialOffer = <T extends ISocialOffer>({
  offer,
  onUpdateOffer,
  candidate,
  expired,
}: IFollowUpSocialOfferProps<T>) => {
  const { matchingChallenges, anchorEl, setAnchorEl, handleProposeSocialOffer, loadingPropose } =
    useFollowUpSocialOffer(offer, onUpdateOffer, candidate);

  const menuItems: ICascadingMenuProps["items"] = [
    {
      label: "Proposée au candidat",
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.PROPOSED, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.PROPOSED}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.PROPOSED}
              />
            ),
          }
        : undefined),
    },
    {
      label: "Candidat intéressé",
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.INTERESTED_CANDIDATE, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.INTERESTED_CANDIDATE}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.INTERESTED_CANDIDATE}
              />
            ),
          }
        : undefined),
    },
    {
      label: "Prescription réalisée",
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.PRESCRIPTION_FILLED, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.PRESCRIPTION_FILLED}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.PRESCRIPTION_FILLED}
              />
            ),
          }
        : undefined),
    },
    {
      label: "Candidat intégré",
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.INTEGRATED_CANDIDATE, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.INTEGRATED_CANDIDATE}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.INTEGRATED_CANDIDATE}
              />
            ),
          }
        : undefined),
    },
    {
      label: "Refus par la structure",
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.REFUSED_STRUCTURE, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.REFUSED_STRUCTURE}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.REFUSED_STRUCTURE}
              />
            ),
          }
        : undefined),
    },
    {
      label: "Refus par le candidat",
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.REFUSED_CANDIDATE, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.REFUSED_CANDIDATE}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.REFUSED_CANDIDATE}
              />
            ),
          }
        : undefined),
    },
  ];

  return (
    <ClickAwayListener mouseEvent="onMouseUp" onClickAway={() => setAnchorEl(null)}>
      <Stack>
        <Chip
          sx={(theme) => ({ borderColor: theme.palette.primary.main })}
          label={
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography color="primary" variant="body2SemiBold">
                Suivi
              </Typography>
              <ArrowDropDownIcon color="primary" />
            </Stack>
          }
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setAnchorEl((prev) => (!prev ? e.currentTarget : null));
          }}
          disabled={expired || loadingPropose}
        />
        <MenuProposeSocialOffer anchorEl={anchorEl} items={menuItems} />
      </Stack>
    </ClickAwayListener>
  );
};
